// src/pages/SponsorOverlay.jsx
// Overlay OBS per gli sponsor, grafica broadcast stile F1. Sfondo TRASPARENTE
// (OBS Browser Source lo gestisce nativamente, nessun chroma-key).
// Varianti selezionabili via route:
//   /obs/sponsors            → carousel  (uno alla volta, wipe F1, a rotazione)
//   /obs/sponsors/banner     → banner    (tutti i loghi affiancati, barra inferiore)
//   /obs/sponsors/ticker     → ticker    (loghi che scorrono in continuo)
//   /obs/sponsors/flash      → flash     (uno sponsor appare pochi secondi ogni N minuti)
//   /obs/sponsor/:id         → single    (un solo sponsor, statico)
// Parametri query opzionali:
//   ?interval=6     secondi per sponsor nel carousel (default 6)
//   ?speed=32       secondi per un giro completo del ticker (default 32)
//   ?plate=0        nasconde targa/etichette e mostra solo il logo "pulito"
//   ?bg=green       sfondo verde chroma-key (default) · bg=transparent · bg=dark
//   ?label=TESTO    testo kicker della targa (default "OFFICIAL PARTNER")
//   Flash:
//   ?show=7         secondi di permanenza a schermo (default 7)
//   ?every=180      secondi tra un'apparizione e la successiva (default 180 = 3 min)
//   ?pos=bl         posizione: bl, br, tl, tr, bc, tc (default bl = basso sinistra)
//   Verticale:
//   ?format=vertical  9:16 per dirette Instagram/YouTube (Browser Source 1080x1920):
//                     grafica compatta nella fascia bassa, sopra i commenti.
//                     ?band=260 altezza della fascia · ?pos=tc in alto, mc al
//                     centro · ?safe=0 usa tutto lo schermo
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import VerticalStage from '../components/obs/VerticalStage';
import { isVerticalFormat, stageBox, verticalPos } from '../utils/obsFormat';
import './SponsorOverlay.css';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);
const REFRESH_MS = 60000; // ricarica lista sponsor: cambi in /sponsor appaiono senza ricaricare la sorgente

const assetUrl = (u) => (!u ? '' : (/^https?:\/\//i.test(u) ? u : `${API_BASE}${u}`));

// Targa broadcast stile F1: barra accento + pannello scuro (kicker + nome) +
// chip bianco col logo, tutto inclinato. `plate=false` → solo il logo, pulito.
// `size` = 'lg' per la versione grande da centro schermo (carousel/single).
function F1Card({ sponsor, plate = true, kicker = 'OFFICIAL PARTNER', size = '' }) {
  if (!sponsor) return null;
  if (!plate) {
    return <img className="f1-bare-logo" src={assetUrl(sponsor.logoUrl)} alt={sponsor.name || 'sponsor'} draggable="false" />;
  }
  return (
    <div className={`f1-card ${size === 'lg' ? 'f1-card--lg' : ''}`}>
      <span className="f1-card-accent" />
      <div className="f1-card-text">
        <span className="f1-card-kicker">{kicker}</span>
        {sponsor.name ? <span className="f1-card-name">{sponsor.name}</span> : null}
      </div>
      <div className="f1-card-stage">
        <img className="f1-card-logo" src={assetUrl(sponsor.logoUrl)} alt={sponsor.name || 'sponsor'} draggable="false" />
      </div>
    </div>
  );
}

/** Sponsor attivi dal backend, ricaricati ogni minuto. */
export function useSponsors() {
  const [sponsors, setSponsors] = useState([]);
  useEffect(() => {
    let alive = true;
    const load = () => {
      fetch(`${API_BASE}/api/sponsors`)
        .then(r => (r.ok ? r.json() : Promise.reject(new Error('sponsor'))))
        .then(j => {
          if (!alive) return;
          setSponsors((j?.sponsors || []).filter(s => s.active !== false && s.logoUrl).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
        })
        .catch(() => { /* mantiene la lista precedente */ });
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);
  return sponsors;
}

/**
 * Striscia sponsor che scorre, pronta da incastonare altrove (dirette social
 * simulate). Stessa grafica di /obs/sponsors/ticker.
 */
export function SponsorTicker({ speed = 32, className = '' }) {
  const sponsors = useSponsors();
  const containerRef = useRef(null);
  const setRef = useRef(null);
  const [copies, setCopies] = useState(2);
  useEffect(() => {
    const measure = () => {
      const cw = containerRef.current?.offsetWidth || 0;
      const sw = setRef.current?.offsetWidth || 0;
      if (cw > 0 && sw > 0) setCopies(prev => (prev === Math.max(1, Math.ceil(cw / sw)) ? prev : Math.max(1, Math.ceil(cw / sw))));
    };
    measure();
    const t = setTimeout(measure, 400);
    let ro;
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) { ro = new ResizeObserver(measure); ro.observe(containerRef.current); }
    return () => { clearTimeout(t); if (ro) ro.disconnect(); };
  }, [sponsors]);
  if (!sponsors.length) return null;
  const totalSets = Math.max(1, copies) * 2;
  return (
    <div className={`sp-ticker ${className}`} ref={containerRef}>
      <div className="sp-ticker-track" style={{ animationDuration: `${speed}s` }}>
        {Array.from({ length: totalSets }).map((_, setIdx) => (
          <div className="sp-ticker-set" key={setIdx} ref={setIdx === 0 ? setRef : null}>
            {sponsors.map((s, i) => (
              <div className="sp-ticker-item" key={`${s.id}-${i}`}>
                <img src={assetUrl(s.logoUrl)} alt={s.name || 'sponsor'} draggable="false" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SponsorOverlay({ variant = 'carousel' }) {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [sponsors, setSponsors] = useState([]);
  const [index, setIndex] = useState(0);

  // Flash: visibile a schermo / in uscita (animazione wipe-out prima di sparire)
  const [flashOn, setFlashOn] = useState(false);
  const [flashLeaving, setFlashLeaving] = useState(false);

  // Ticker: quante volte ripetere la sequenza per coprire almeno la larghezza
  // schermo (con pochi sponsor va replicata più volte, altrimenti resta spazio
  // vuoto). Il valore è ricalcolato misurando lo schermo e un blocco di loghi.
  const tickerContainerRef = useRef(null);
  const tickerSetRef = useRef(null);
  const [tickerCopies, setTickerCopies] = useState(2);

  const interval = Math.max(2, Number(params.get('interval')) || 6);
  const tickerSpeed = Math.max(8, Number(params.get('speed')) || 32);
  const showPlate = params.get('plate') !== '0';
  const bg = (params.get('bg') || 'green').toLowerCase();   // green screen di default
  const kicker = params.get('label') || 'OFFICIAL PARTNER';
  // parametri flash
  const flashShow = Math.max(3, Number(params.get('show')) || 7);
  const flashEvery = Math.max(flashShow + 5, Number(params.get('every')) || 180);
  const posRaw = (params.get('pos') || 'bl').toLowerCase();
  const pos = ['bl', 'br', 'tl', 'tr', 'bc', 'tc'].includes(posRaw) ? posRaw : 'bl';
  const vertical = isVerticalFormat(params);

  // Sfondo pagina trasparente per OBS (o verde/scuro se richiesto), ripristinato in uscita.
  useEffect(() => {
    const html = document.documentElement, body = document.body;
    const prev = { htmlBg: html.style.background, bodyBg: body.style.background };
    const color = bg === 'green' ? '#00ff00' : bg === 'dark' ? '#0a0a0a' : 'transparent';
    html.style.background = color;
    body.style.background = color;
    return () => { html.style.background = prev.htmlBg; body.style.background = prev.bodyBg; };
  }, [bg]);

  // Carica gli sponsor attivi e li ricarica periodicamente.
  useEffect(() => {
    let alive = true;
    const load = () => {
      fetch(`${API_BASE}/api/sponsors`)
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(j => {
          if (!alive) return;
          const list = (j?.sponsors || [])
            .filter(s => s.active !== false && s.logoUrl)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          setSponsors(list);
        })
        .catch(() => { /* mantiene la lista precedente */ });
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Rotazione carousel.
  useEffect(() => {
    if (variant !== 'carousel' || sponsors.length <= 1) return;
    const t = setInterval(() => setIndex(i => (i + 1) % sponsors.length), interval * 1000);
    return () => clearInterval(t);
  }, [variant, sponsors.length, interval]);

  // Ciclo flash: appare per flashShow secondi (con wipe-out finale), poi sparisce
  // e passa allo sponsor successivo; si ripete ogni flashEvery secondi.
  // La prima apparizione avviene pochi secondi dopo il caricamento.
  useEffect(() => {
    if (variant !== 'flash' || !sponsors.length) return;
    let timers = [];
    const cycle = () => {
      setFlashLeaving(false);
      setFlashOn(true);
      timers.push(setTimeout(() => setFlashLeaving(true), Math.max(500, (flashShow - 0.7) * 1000)));
      timers.push(setTimeout(() => {
        setFlashOn(false);
        setFlashLeaving(false);
        setIndex(i => (i + 1) % Math.max(1, sponsors.length));
      }, flashShow * 1000));
    };
    timers.push(setTimeout(cycle, 4000));
    const loop = setInterval(cycle, flashEvery * 1000);
    return () => { timers.forEach(clearTimeout); clearInterval(loop); setFlashOn(false); setFlashLeaving(false); };
  }, [variant, sponsors.length, flashShow, flashEvery]);

  // Mantiene l'indice valido se la lista si accorcia.
  useEffect(() => {
    if (index >= sponsors.length) setIndex(0);
  }, [sponsors.length, index]);

  // Ticker: misura schermo e larghezza di un blocco di loghi per capire quante
  // ripetizioni servono a riempire tutta la larghezza (scorrimento seamless).
  useEffect(() => {
    if (variant !== 'ticker') return undefined;
    const measure = () => {
      const cw = tickerContainerRef.current?.offsetWidth || 0;
      const sw = tickerSetRef.current?.offsetWidth || 0;
      if (cw > 0 && sw > 0) {
        const needed = Math.max(1, Math.ceil(cw / sw));
        setTickerCopies(prev => (prev === needed ? prev : needed));
      }
    };
    measure();
    const t = setTimeout(measure, 400); // ri-misura dopo il caricamento dei loghi
    let ro;
    if (typeof ResizeObserver !== 'undefined' && tickerContainerRef.current) {
      ro = new ResizeObserver(measure);
      ro.observe(tickerContainerRef.current);
    }
    return () => { clearTimeout(t); if (ro) ro.disconnect(); };
  }, [variant, sponsors]);

  const single = useMemo(() => sponsors.find(s => s.id === id) || null, [sponsors, id]);
  const rootCls = `sp-ovl sp-ovl--${variant} sp-ovl--bg-${bg === 'green' || bg === 'dark' ? bg : 'transparent'}${vertical ? ' sp-ovl--vertical' : ''}`;

  // Verticale: stesso contenuto dentro il palco 9:16, nella fascia bassa sopra i
  // commenti (?pos=tc in alto, mc al centro).
  const stageFor = (content) => {
    if (!vertical) return content;
    const vpos = verticalPos(params.get('pos'), 'bottom');
    const hpos = variant === 'flash' ? (pos.endsWith('l') ? 'l' : pos.endsWith('r') ? 'r' : 'c') : 'c';
    return (
      <VerticalStage box={stageBox(params, vpos)} className={`ov-stage--${vpos} sp-vstage sp-vstage--${variant} sp-vstage--h${hpos}`}>
        {content}
      </VerticalStage>
    );
  };

  // Nessuno sponsor: overlay completamente vuoto (nessun artefatto in scena).
  if (!sponsors.length && variant !== 'single') {
    return <div className={rootCls} />;
  }

  if (variant === 'single') {
    return (
      <div className={rootCls}>
        {single ? stageFor(
          <div className="f1-pop f1-pop--in">
            <F1Card sponsor={single} plate={showPlate} kicker={kicker} size="lg" />
          </div>
        ) : null}
      </div>
    );
  }

  if (variant === 'flash') {
    const cur = sponsors[index % sponsors.length];
    return (
      <div className={vertical ? rootCls : `${rootCls} sp-flash-pos-${pos}`}>
        {flashOn && cur ? stageFor(
          <div className={`f1-pop ${flashLeaving ? 'f1-pop--out' : 'f1-pop--in'}`}>
            <F1Card sponsor={cur} plate={showPlate} kicker={kicker} />
          </div>
        ) : null}
      </div>
    );
  }

  if (variant === 'banner') {
    return (
      <div className={rootCls}>
        {stageFor(<div className="sp-banner">
          {showPlate && (
            <span className="sp-banner-label"><span>PARTNERS</span></span>
          )}
          <div className="sp-banner-track">
            {sponsors.map(s => (
              <div className="sp-banner-item" key={s.id}>
                <img src={assetUrl(s.logoUrl)} alt={s.name || 'sponsor'} draggable="false" />
              </div>
            ))}
          </div>
        </div>)}
      </div>
    );
  }

  if (variant === 'ticker') {
    // Un "blocco" = la sequenza sponsor ripetuta tickerCopies volte (largo almeno
    // quanto lo schermo). La traccia contiene 2 blocchi identici e scorre di -50%:
    // così è sempre piena e l'anello è senza stacchi.
    const blocks = Math.max(1, tickerCopies);
    const totalSets = blocks * 2;
    return (
      <div className={rootCls}>
        {stageFor(<div className="sp-ticker" ref={tickerContainerRef}>
          <div className="sp-ticker-track" style={{ animationDuration: `${tickerSpeed}s` }}>
            {Array.from({ length: totalSets }).map((_, setIdx) => (
              <div className="sp-ticker-set" key={setIdx} ref={setIdx === 0 ? tickerSetRef : null}>
                {sponsors.map((s, i) => (
                  <div className="sp-ticker-item" key={`${s.id}-${i}`}>
                    <img src={assetUrl(s.logoUrl)} alt={s.name || 'sponsor'} draggable="false" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>)}
      </div>
    );
  }

  // carousel (default): targhe impilate, solo la corrente visibile, wipe F1 a ogni cambio.
  return (
    <div className={rootCls}>
      {stageFor(<div className="sp-carousel">
        {sponsors.map((s, i) => (
          <div key={s.id} className={`sp-carousel-slide ${i === index ? 'is-active' : ''}`}>
            <F1Card sponsor={s} plate={showPlate} kicker={kicker} size="lg" />
          </div>
        ))}
        {showPlate && sponsors.length > 1 && (
          <div className="sp-carousel-progress" key={index}>
            <span className="sp-carousel-progress-bar" style={{ animationDuration: `${interval}s` }} />
          </div>
        )}
      </div>)}
    </div>
  );
}
